import { IDexTrade, ITicker } from '@kattana-io/common';
import {
  IMobileTableScema,
  formatMobileTableData,
} from '../../../../helpers/formatters';
import PairIcons from '../../../../molecules/PairIcons';
import useTicker from '../../../../hooks/useTicker';
import Text from '../../../../atoms/Text';
import { decorateExchangeName } from '../../../../helpers/decorators';
import {
  formatTimestamp,
  formatTx,
} from '../../../../components/DexTrades/formatters';
import NumberDisplay from '../../../../molecules/NumberDisplay';
import { onTxScan } from '../../../../helpers/addressUtils';
import Loader from '../../../../components/common/Loader';
import TableHeaderMobile from '../../../../molecules/Table/mobile/TableHeader';
import TableCardMobile from '../../../../molecules/Table/mobile/TableCard';

type tickersMap = Record<string, ITicker>;

const buyClassName = 'text--green-600';
const sellClassName = 'text--red-600';

const getAmounts = (trade: IDexTrade, item: ITicker) => {
  const ticker = useTicker(item);
  // if pair flipped - buy/sell will be reversed
  const isBuy = trade.buy === 1 ? !item.flipped : item.flipped;

  // get amounts for base and quote considering flipped
  const baseAmount = Number(item.flipped ? trade.amount1 : trade.amount0);
  const quoteAmount = Number(item.flipped ? trade.amount0 : trade.amount1);

  // get in and out amounts depending on trade was buy or sell
  const amountIn = isBuy
    ? quoteAmount / 10 ** ticker.quote_decimals
    : baseAmount / 10 ** ticker.base_decimals;

  const amountOut = isBuy
    ? baseAmount / 10 ** ticker.base_decimals
    : quoteAmount / 10 ** ticker.quote_decimals;

  // get in and out tokens depending on trade was buy or sell
  const amountInToken = isBuy ? ticker.quote : ticker.base;
  const amountOutToken = isBuy ? ticker.base : ticker.quote;

  return { amountIn, amountOut, amountInToken, amountOutToken };
};

interface IWalletTradesTableProps {
  network: string;
  timezone?: string;
  trades: IDexTrade[];
  tickers: tickersMap;
  loading: boolean;
  onTickerClick: (ticker: ITicker) => void;
}

const WalletTradesTableMobile = ({
  tickers,
  trades,
  timezone,
  loading,
  network,
  onTickerClick,
}: IWalletTradesTableProps) => {
  const schema: IMobileTableScema = {
    row_1: {
      item_1: {
        name: 'Pair',
        accessor: 'ticker',
        parser: (_, row: IDexTrade) => {
          const key = `${row.chain}-${row.pair}`;
          const item = tickers[key];
          const ticker = useTicker(item);
          const isBuy = row.buy === 1 ? !item.flipped : item.flipped;

          return (
            <span className='ticker__cell' onClick={() => onTickerClick(item)}>
              <PairIcons {...{ ...ticker, pair: row.pair }} />
              <div className='trades__ticker_description'>
                <div className='trades__ticker_description__name'>
                  <Text size={12} className='text--grey-200 text--weight-600'>
                    {ticker.name}
                  </Text>
                  <div
                    className={`icon-cl__get-to-r ${
                      isBuy ? buyClassName : sellClassName
                    }`}
                  />
                </div>
                <Text size={12} className='text--grey-600'>
                  {decorateExchangeName(ticker.exchange, ticker.chain)}
                </Text>
              </div>
            </span>
          );
        },
        width: 160,
      },
      item_2: {
        name: 'Value USD',
        accessor: 'value_usd',
        parser: (value, row: IDexTrade) => {
          const key = `${row.chain}-${row.pair}`;
          const item = tickers[key];
          const isBuy = row.buy === 1 ? !item.flipped : item.flipped;
          return (
            <NumberDisplay
              value={value}
              endSymbol=' $'
              textProps={{
                size: 14,
                className: isBuy ? buyClassName : sellClassName,
              }}
            />
          );
        },
      },
    },
    row_2: {
      item_1: {
        name: 'Time',
        accessor: 'timestamp',
        parser: (value) => formatTimestamp(value, timezone),
        width: 160,
      },
      item_2: {
        name: 'Type',
        accessor: 'buy',
        parser: (value, row: IDexTrade) => {
          const key = `${row.chain}-${row.pair}`;
          const item = tickers[key];
          const isBuy = value === 1 ? !item.flipped : item.flipped;
          return (
            <Text size={14} className={isBuy ? buyClassName : sellClassName}>
              {isBuy ? 'Buy' : 'Sell'}
            </Text>
          );
        },
      },
    },
    row_3: {
      item_1: {
        name: 'Amount In',
        accessor: 'amount0',
        parser: (_, row: IDexTrade) => {
          const key = `${row.chain}-${row.pair}`;
          const item = tickers[key];
          const { amountIn, amountInToken } = getAmounts(row, item);
          return (
            <NumberDisplay
              value={amountIn}
              endSymbol={` ${amountInToken}`}
              string
            />
          );
        },
        width: 160,
        string: true,
      },
      item_2: {
        name: 'Amount Out',
        accessor: 'amount1',
        parser: (_, row: IDexTrade) => {
          const key = `${row.chain}-${row.pair}`;
          const item = tickers[key];
          const { amountOut, amountOutToken } = getAmounts(row, item);
          return (
            <NumberDisplay
              value={amountOut}
              endSymbol={` ${amountOutToken}`}
              string
            />
          );
        },
        string: true,
      },
    },
    row_4: {
      item_1: {
        name: 'Price USD',
        accessor: 'price_a_usd',
        parser: (_, row: IDexTrade) => {
          const key = `${row.chain}-${row.pair}`;
          const item = tickers[key];
          const isBuy = row.buy === 1 ? !item.flipped : item.flipped;
          return (
            <NumberDisplay
              value={item.flipped ? row.price_b_usd : row.price_a_usd}
              endSymbol=' $'
              textProps={{
                size: 14,
                className: isBuy ? buyClassName : sellClassName,
              }}
            />
          );
        },
        width: 160,
      },
      item_2: {
        name: 'Tx Hash',
        accessor: 'tx',
        parser: (value) => formatTx(value),
        onClick: (row) => onTxScan(row.tx, network),
        color: 'navy',
      },
    },
  };

  return (
    <div className='dex_trades_mobile__table'>
      {loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : !trades.length ? (
        <div className='centrify text text--16'>No data found</div>
      ) : (
        <>
          <TableHeaderMobile schema={schema} />
          {formatMobileTableData({
            data: trades,
            schema,
            getColorForItem: () => 'grey',
            keyName: ['tx', 'order'],
          })?.map((row) => (
            <TableCardMobile key={row.key} data={row} />
          ))}
        </>
      )}
    </div>
  );
};

export default WalletTradesTableMobile;
